import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import {
  type IntegrationProviderType,
  mutateSaveIntegrationConnectionState,
  useIntegrationProviderDetails,
} from '@/api/integration';
import APITokenConnectionDialog from '@/app/integrations/[provider]/APITokenConnectionDialog';
import useDenyConnectionCreation from '@/app/integrations/useCanCreateConnection';
import PlusIcon from '@/assets/svg/bx-plus.svg?react';
import { Button } from '@/components/buttons';
import { Separator } from '@/components/Separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { Badge, Text, Title } from '@/components/typography';
import { useNotification } from '@/providers/Notification';

import IntegrationStatus from '../IntegrationStatus';
import { getIntegrationProviderDisplayStatus } from '../integrationUtil';
import {
  getProviderAuthMethodById,
  getProviderLogoById,
  getProviderShortDescriptionById,
} from '../providerUtil';
import type { AuthMethodAPIToken } from '../types';
import IntegrationDetailConnectionList from './IntegrationDetailConnectionList';

type Props = {
  provider: IntegrationProviderType;
};

const IntegrationDetailRail = ({ provider }: Props) => {
  const { pushNotification } = useNotification();
  const { mutate } = useIntegrationProviderDetails(provider.id);
  const [isAddingConnection, setIsAddingConnection] = useState(false);
  const [authMethodAPIToken, setAuthMethodAPIToken] = useState<AuthMethodAPIToken | null>(null);
  const denyConnectionCreation = useDenyConnectionCreation(provider.category);

  const numConnections = provider?.connections.length ?? 0;
  const workingConnections = provider?.connections.filter((conn) => conn.status === 'active');
  const hasNoConnections = numConnections === 0;
  const numWorkingConnections = workingConnections?.length ?? 0;
  const numBrokenConnections = numConnections - numWorkingConnections;

  const handleOAuthLogin = async () => {
    try {
      setIsAddingConnection(true);

      if (!provider?.startUrl) {
        throw new Error('Start URL is not set for this provider');
      }

      const url = new URL(provider.startUrl);
      const state = url.searchParams.get('state');

      if (!state) {
        throw new Error('State is not set for this provider');
      }

      const success = await mutateSaveIntegrationConnectionState(state);
      if (success) {
        window.location.href = provider.startUrl;
      } else {
        throw new Error('Failed to save connection state');
      }
    } catch (e) {
      console.error(e);
      pushNotification({
        type: 'error',
        message: t`An error occurred while trying to connect to ${provider?.name}`,
      });
    } finally {
      setIsAddingConnection(false);
    }
  };

  const handleConnectionCreated = async () => {
    setAuthMethodAPIToken(null);
    mutate();
  };

  const handleAddConnection = async () => {
    const authMethod = getProviderAuthMethodById(provider?.id);

    if (!authMethod) {
      throw new Error('No authentication method found for this provider');
    }

    if (authMethod.type === 'oauth') {
      await handleOAuthLogin();
    } else if (authMethod.type === 'api-token') {
      setAuthMethodAPIToken(authMethod as AuthMethodAPIToken);
    }
  };

  const handleDisconnect = async () => {
    mutate();
  };

  return (
    <div
      data-testid="integration-detail-rail"
      className="flex max-w-90 min-w-90 flex-col items-start gap-2"
    >
      <div className="flex flex-col gap-7">
        {getProviderLogoById(provider?.id)}
        <Title level="3" weight="semi-bold">
          {provider?.name}
        </Title>
      </div>
      {provider?.multiConnection ? (
        <>
          {numWorkingConnections > 0 && (
            <IntegrationStatus status="active" count={numWorkingConnections} />
          )}
          {numBrokenConnections > 0 && (
            <IntegrationStatus status="broken" count={numBrokenConnections} />
          )}
          {hasNoConnections && (
            <>
              <IntegrationStatus status="notConnected" />
              <Text variant="body2" color="dark-grey">
                {getProviderShortDescriptionById(provider?.id)}
              </Text>
            </>
          )}
        </>
      ) : (
        <>
          <IntegrationStatus status={getIntegrationProviderDisplayStatus(provider.connections)} />
          <Text variant="body2" color="dark-grey">
            {getProviderShortDescriptionById(provider?.id)}
          </Text>
        </>
      )}
      {provider?.available ? (
        <>
          <IntegrationDetailConnectionList provider={provider} onDisconnect={handleDisconnect} />
          <Separator size="large" />
          {(hasNoConnections || provider?.multiConnection) && (
            <Tooltip open={denyConnectionCreation ? undefined : false}>
              <TooltipTrigger
                render={
                  <Button
                    disabled={denyConnectionCreation}
                    isLoading={isAddingConnection}
                    onClick={handleAddConnection}
                  >
                    <PlusIcon />
                    <Trans>{provider?.name} Account</Trans>
                  </Button>
                }
              />
              <TooltipContent>
                <Trans>
                  You have already connected a CRM account. Only one CRM can be connected to your
                  instance.
                </Trans>
              </TooltipContent>
            </Tooltip>
          )}
        </>
      ) : (
        <>
          <Separator size="large" />
          <Badge size="large" color="info">
            <Trans>Coming soon</Trans>
          </Badge>
        </>
      )}
      <APITokenConnectionDialog
        providerId={provider?.id}
        authMethod={authMethodAPIToken}
        onClose={() => setAuthMethodAPIToken(null)}
        onOk={handleConnectionCreated}
      />
    </div>
  );
};

export default IntegrationDetailRail;
